import type {
  IActivityProductivityRow,
  IConfidenceBand,
  IExpectedVsActual,
  ILocationDrillDown,
  ILocationProductivityRow,
  IProductivityCoverage,
  IProductivityException,
  IProductivityMetric,
  IProductivitySpatialInsight,
  ISubcontractorProductivityRow,
  IWorkersProductivityRow,
  ProductivityGranularity,
} from '@interfaces/IProductivity';
import { authedFetch, UnauthorizedError } from '@apis/auth';

/**
 * Productivity BFF client. The backend assembles each part server-side and
 * returns it in the FE shape, so the page just renders.
 */
export interface IProductivitySummary {
  metrics: IProductivityMetric[];
  insight: IProductivitySpatialInsight;
  exceptions: IProductivityException[];
  coverage: IProductivityCoverage;
}

export type IProductivityBreakdown =
  | {
      dimension: 'location';
      rows: ILocationProductivityRow[];
      drillDowns: Record<string, ILocationDrillDown>;
    }
  | { dimension: 'subcontractors'; rows: ISubcontractorProductivityRow[] }
  | { dimension: 'activities'; rows: IActivityProductivityRow[] }
  | { dimension: 'workers'; rows: IWorkersProductivityRow[] };

export type BreakdownDimension = IProductivityBreakdown['dimension'];

export interface IProductivityRangeParams {
  dateFrom: string; // YYYY-MM-DD in the site's tz
  dateTo: string;   // YYYY-MM-DD in the site's tz
}

async function handle(res: Response): Promise<never> {
  const body = await res.json().catch(() => null);
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`request failed: ${res.status} ${body?.error?.message ?? ''}`);
}

export async function fetchProductivitySummary(
  siteId: string,
  signal?: AbortSignal,
): Promise<IProductivitySummary> {
  // The summary is a current snapshot — no range; only the breakdown is scoped.
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/productivity/summary`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as IProductivitySummary;
}

/** The timeframe-scoped payload: chart + confidence mix in ONE request — they
 *  always change together — plus the open tab's rows when the request named
 *  one, so a timeframe switch is a single request. */
export interface IProductivityPeriod {
  earnedVsActual: IExpectedVsActual;
  confidenceMix: IConfidenceBand[];
  breakdown?: IProductivityBreakdown;
}

export async function fetchProductivityPeriod(
  siteId: string,
  range: IProductivityRangeParams,
  granularity: ProductivityGranularity,
  tab?: BreakdownDimension,
  signal?: AbortSignal,
): Promise<IProductivityPeriod> {
  const qs = new URLSearchParams({
    dateFrom: range.dateFrom,
    dateTo: range.dateTo,
    granularity,
    ...(tab ? { tab } : {}),
  });
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/productivity/period?${qs.toString()}`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as IProductivityPeriod;
}

export async function fetchProductivityBreakdown(
  siteId: string,
  dimension: BreakdownDimension,
  range: IProductivityRangeParams,
  signal?: AbortSignal,
): Promise<IProductivityBreakdown> {
  const qs = new URLSearchParams({ dateFrom: range.dateFrom, dateTo: range.dateTo });
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/productivity/breakdown/${dimension}?${qs.toString()}`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as IProductivityBreakdown;
}
